<template>
  <div class="content terminated-accounts-backups">
    <div class="row animated fadeIn">
      <div class="col-md-12">

        <!-- Downloaded backups table (below backups table) -->
        <div class="card table-card downloaded-card">
          <div class="card-header d-flex justify-content-center align-items-center">
            <h5 class="card-title mb-0">
              <i class="fa fa-download mr-2"></i>
              Ready To Download Backups
            </h5>
          </div>
          <div class="card-body p-0">
            <div v-if="downloadedBackupsLoading" class="downloaded-backups-loader text-center py-5">
              <i class="fa fa-spinner fa-spin fa-2x text-muted mb-2"></i>
              <p class="mb-0 text-muted">Fetching downloaded backups...</p>
            </div>
            <div v-else class="table-responsive pl-10 pr-10 pt-10">
              <table id="datatable" class="table backups-table downloaded-table mb-0 ready-downloads-datatable">
                <thead class="thead-light">
                  <tr>
                    <th>Domain</th>
                    <th>Service Id</th>
                    <th>Backup</th>
                    <th class="text-center">Requested at</th>
                    <th class="text-center">Status</th>
                    <th class="text-center">Download</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in downloadedList" :key="item.id || item.domain + item.createdAt">
                    <td>
                      <span class="">{{ item.domain }}</span>
                    </td>
                    <td>{{item.serviceId}}</td>
                    <td>
                      <span class="text-muted">{{ formatDate(item.Time)}}</span>
                    </td>
                    <td class="text-center text-muted">
                      {{ formatDate(item.createdAt) }}
                    </td>
                    <td class="text-center">
                      <span class="badge badge-pill status-badge" :class="item.status === 'complete' ? 'badge-success' : 'badge-default'">
                        <i v-if="item.status === 'running'" class="fa fa-spinner fa-spin mr-5"></i>{{ item.status }}
                      </span>
                    </td>
                    <td class="text-center">
                      <span
                        v-if="item.status === 'complete' && isLatestBackupForDomain(item.domain, index)"
                        class="d-inline-block"
                      >
                        <button
                          @click="openUrl(item.URL)"
                          class="btn btn-sm btn-info btn-outline-info"
                        >
                          <i class="fa fa-download"></i>
                        </button>
                      </span>
                    </td>
                  </tr>
                  <tr v-if="downloadedList.length === 0">
                    <td colspan="5" class="empty-state">
                      <i class="fa fa-folder-open fa-3x text-muted mb-3"></i>
                      <p class="mb-0 text-muted">No backups downloaded.</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <!-- Form card -->
        <div class="card form-card">
          <div class="card-header form-card-header">
            <h4 class="card-title mb-0">Terminated Accounts Backups</h4>
          </div>
          <div class="card-body form-card-body">
            <p class="form-description">Enter a service ID to deploy the backup agent and list available backups.</p>
            <form @submit.prevent="onSubmit" class="terminated-form">
              <div class="form-group">
                <label for="serviceId">Service ID</label>
                <input
                    id="serviceId"
                    v-model.trim="serviceId"
                    type="text"
                    class="form-control"
                    placeholder="Enter service ID"
                    required
                    :disabled="loading"
                    @keydown.space.prevent
                />
              </div>
              <div class="form-actions">
                <span
                    class="terminated-backup-tooltip d-inline-block"
                    :data-tippy-content="deployButtonTooltip"
                >
                  <button
                      type="submit"
                      class="btn btn-primary btn-sm btn-submit mt-0"
                      :disabled="loading || !serviceId || hasDownloadInProgress || isDeployLockedByOther"
                  >
                    <span v-if="loading" class="d-inline-flex align-items-center">
                      <i class="fa fa-spinner fa-spin mr-3"></i>
                      {{ stepLabel }}
                    </span>
                    <span v-else><i class="fa fa-cloud-upload mr-1"></i> Deploy & Fetch Backups</span>
                  </button>
                </span>
                <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm ml-2 mt-0"
                    :disabled="loading"
                    @click="resetServiceId"
                >
                  <i class="fa fa-undo mr-1"></i> Reset
                </button>
                <br>
                <small v-if="loading" class="text-muted">Please wait, this might take a few minutes.</small>
              </div>
            </form>

            <!-- Status messages -->
            <div v-if="errorMessage" class="status-message bg-danger mt-3">
              <i class="fa fa-times-circle"></i>
              <span>{{ errorMessage }}</span>
            </div>
            <div v-if="isDeployLockedByOther" class="status-message bg-warning mt-3">
              <i class="fa fa-exclamation-circle"></i>
              <span>Please wait, {{ deployLock.lockedByName }} is currently working on this process.</span>
            </div>
          </div>
        </div>

        <!-- Backups table card (only after we have data or attempted fetch) -->
        <div v-if="backupList.length > 0" class="card table-card">
          <div class="card-header d-flex justify-content-center align-items-center">
            <h5 class="card-title mb-0">
              <i class="fa fa-database mr-2"></i>
              Backups For Service - {{ serviceId }}
            </h5>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table backups-table mb-0">
                <thead class="thead-light">
                <tr>
                  <th>Domain</th>
                  <th>Backups</th>
                  <th class="text-center" style="width: 140px;">Action</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(row, rowIndex) in backupList" :key="row.domain + rowIndex">
                  <td>
                    <span>{{ row.domain }}</span>
                  </td>
                  <td>
                    <select
                        v-model="selectedBackup[row.domain]"
                        class="form-control form-control-sm backup-select"
                        :disabled="!row.Snapshots || row.Snapshots.length === 0"
                    >
                      <option :value="null" disabled>
                        {{ row.Snapshots && row.Snapshots.length ? 'Select backup' : 'No snapshots' }}
                      </option>
                      <option
                          v-for="(snap, idx) in (row.Snapshots || [])"
                          :key="snap.ObjID || idx"
                          :value="snap"
                      >
                        {{ snapshotLabel(snap) }}
                      </option>
                    </select>
                  </td>
                  <td class="text-center">
                    <div class="terminated-backup-tooltip" :data-tippy-content="hasDownloadInProgress ? downloadInProgressTooltip : null"
                    >
                      <button
                          type="button"
                          class="btn btn-sm btn-info btn-outline-info"
                          :disabled="downloadLoading === row.domain || hasDownloadInProgress || isDeployLockedByOther"
                          @click="startDownload(row)"
                      >
                        <span v-if="downloadLoading === row.domain" class="d-inline-flex align-items-center">
                          <i class="fa fa-spinner fa-spin mr-1"></i>
                        </span>
                        <span v-else>
                            Generate Download Link
                        </span>
                      </button>
                    </div>

                  </td>
                </tr>
                <tr v-if="backupList.length === 0 && step === 'done'">
                  <td colspan="3" class="empty-state">
                    <i class="fa fa-inbox fa-3x text-muted mb-3"></i>
                    <p class="mb-0 text-muted">No backups found for this service.</p>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import {db} from "@/firebase/init";

const DEPLOY_LOCK_REF = db.collection("terminatedAccountsDeployLock").doc("global");

export default {
  name: "TerminatedAccountsBackups",
  mounted() {
    this.subscribeDownloadedBackups();
    this.subscribeDeployLock();
    this.$nextTick(() => this.initDisabledTooltips());
  },
  updated() {
    this.$nextTick(() => this.initDisabledTooltips());
  },
  data() {
    return {
      serviceId: "",
      loading: false,
      step: "idle", // idle | deploying | agent_deployed | fetching_backups | done
      errorMessage: "",
      backupList: [], // [{ domain, Snapshots: [{ ObjID, Time, Files, Dirs, Sz, Desc, IsOwn }] }]
      selectedBackup: {}, // { 'domain': snapshotIndex }
      downloadLoading: null, // domain when that row is downloading
      downloadedList: [], // from Firebase collection downloadedBackupsForTerminatedAccounts
      downloadedBackupsListener: null,
      downloadedBackupsLoading: true,
      downloadInProgressTooltip: "A backup download is in progress. Please wait for it to complete.",
      deployLock: { locked: false, lockedBy: null, lockedAt: null, lockedByName: null },
      deployLockListener: null,
    };
  },
  watch: {
    downloadedList() {
      this.initReadyDownloadsDataTable()
    }
  },
  computed: {
    user() {
      return this.$store.getters.user || {};
    },
    currentUserEmail() {
      return (this.user && this.user.email) || "";
    },
    isDeployLockedByOther() {
      return this.deployLock.locked && this.deployLock.lockedBy && this.deployLock.lockedBy !== this.currentUserEmail;
    },
    stepLabel() {
      if (this.step === "deploying") return "Deploying agent...";
      if (this.step === "agent_deployed" || this.step === "fetching_backups") return "Fetching backups...";
      return "Please wait...";
    },
    hasDownloadInProgress() {
      return this.downloadedList.some((item) => item.status && item.status !== "complete");
    },
    deployButtonTooltip() {
      if (this.loading || !this.serviceId || this.hasDownloadInProgress) {
        if (this.hasDownloadInProgress) return this.downloadInProgressTooltip;
        if (this.loading) return "Please wait for the current operation to finish.";
        if (!this.serviceId) return "Enter a service ID to continue.";
      }
      return null;
    },
  },
  methods: {
    initReadyDownloadsDataTable(msg = "No backups downloaded.") {
      /* eslint-disable */
      if($.fn.DataTable.isDataTable('#datatable')) {
        $(`#datatable`).DataTable().destroy();
      }
      $(function () {
        $('[data-toggle="tooltip"]').tooltip();
      });
      setTimeout(function () {
        $(`#datatable`).dataTable({
          pagingType: "full_numbers",
          paging: true,
          destroy: true,
          lengthMenu: [[5, 10, 25, 50], [5, 10, 25, 50]],
          pageLength: 5, // show 5 rows by default
          retrieve: true,
          deferRender: true,
          stateSave: false,
          "bInfo" : false,
          "ordering": false,
        });
      }, 1000)
      /* eslint-enable */
    },
    isLatestBackupForDomain(domain, index) {
      if (!domain) return false;
      // downloadedList is sorted by expiryDate desc, so the first
      // occurrence of a domain represents its most recent backup.
      return !this.downloadedList.slice(0, index).some((row) => row.domain === domain);
    },
    async resetServiceId() {
      this.serviceId = "";
      this.errorMessage = "";
      this.backupList =[]
      await this.releaseDeployLock()
    },
    getSelectTooltip(row) {
      const disabled = !row.Snapshots || row.Snapshots.length === 0 || this.hasDownloadInProgress;
      if (!disabled) return null;
      if (this.hasDownloadInProgress) return this.downloadInProgressTooltip;
      return "No backups available for this domain.";
    },
    getRowDownloadTooltip(row) {
      const disabled = this.downloadLoading === row.domain || this.hasDownloadInProgress;
      if (!disabled) return null;
      if (this.downloadLoading === row.domain) return "Download in progress...";
      return this.downloadInProgressTooltip;
    },
    initDisabledTooltips() {
      if (!this.$el) return;
      const elms = this.$el.querySelectorAll(".terminated-backup-tooltip");
      elms.forEach((el) => {
        if (el._tippy) el._tippy.destroy();
        const content = el.getAttribute("data-tippy-content");
        if (content) {
          tippy(el, {
            content,
            placement: "top",
            animation: "fade",
            arrow: true,
          });
        }
      });
    },
    openUrl(URL) {
      window.open(URL)
    },
    snapshotLabel(snap) {
      if (!snap) return "—";
      const time = snap.Time ? moment(snap.Time).format("DD MMM YYYY, HH:mm") : snap.ObjID || "Snapshot";
      const size = snap.Sz != null ? this.formatSize(snap.Sz) : "";
      return time;
    },
    formatDate(val) {
      if (!val) return "—";
      return moment(val).format("DD MMM YYYY, hh:mm A");
    },
    formatSize(bytes) {
      if (bytes == null) return "";
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB";
      return (bytes / (1024 * 1024 * 1024)).toFixed(1) + " GB";
    },
    getSelectedSnapshot(row) {
      const idx = this.selectedBackup[row.domain];
      if (idx == null || !row.Snapshots || !row.Snapshots[idx]) return null;
      return row.Snapshots[idx];
    },
    async onSubmit() {
      if (!this.serviceId) return;
      this.loading = true;
      this.errorMessage = "";
      this.step = "deploying";
      this.backupList = [];
      this.selectedBackup = {};

      let lockAcquired = false;
      try {
        if (this.isDeployLockedByOther) {
          // this.errorMessage = 'Deploy is already in progress by another user. Please wait.'
          // this.showToast(
          //   { message: "Deploy is already in progress by another user. Please wait.", duration: 4000 },
          //   true,
          //   "warning"
          // );
          return;
        }
        const acquired = await this.acquireDeployLock();

        lockAcquired = true;
        // 1. Deploy agent
        const deployRes = await this.$http.post("/lxd/n8n-apis-for-lxd-migration", {
          grant_type: "token",
          serviceId: this.serviceId,
          action: "deploy-agent",
        });

        if (!deployRes.data || !deployRes.data.success) {
          this.errorMessage = (deployRes.data && deployRes.data.message) || "Agent deployment failed.";
          // this.showToast({ message: this.errorMessage, duration: 3000 }, true, "danger");
          this.step = "done";
          return;
        }

        this.step = "agent_deployed";
        this.showToast({ message: 'Backup agent deployed, Now fetching backups.', duration: 3000 }, true, "success");

        // 2. List backups
        const listRes = await this.$http.post("/lxd/n8n-apis-for-lxd-migration", {
          grant_type: "token",
          action: "list-backups",
          serviceId: this.serviceId,
        });

        this.step = "done";

        if (listRes.data && listRes.data.success && Array.isArray(listRes.data.data)) {
          this.backupList = listRes.data.data;
          this.backupList.forEach((row) => {
            if (row.Snapshots && row.Snapshots.length) {
              this.$set(this.selectedBackup, row.domain, row.Snapshots[0]);
            }
          });
        } else {
          this.backupList = [];
        }
      } catch (e) {
        console.error(e);
        this.step = "done";
        this.errorMessage =
          (e.response && e.response.data && e.response.data.message) ||
          "Something went wrong.";
        this.showToast({ message: this.errorMessage, duration: 3000 }, true, "danger");
        await this.releaseDeployLock();

      } finally {
        this.loading = false;
      }
    },
    async acquireDeployLock() {
      const currentUserEmail = this.currentUserEmail;
      if (!currentUserEmail) return false;
      try {
        await DEPLOY_LOCK_REF.update({
          locked: true,
          lockedBy: currentUserEmail,
          lockedAt: new Date().toISOString(),
          lockedByName: this.user.firstName,
          serviceId: this.serviceId,
        })
        return false
      } catch (e) {
        console.error("acquireDeployLock error:", e);
        return false;
      }
    },
    async releaseDeployLock() {
      try {
        const snap = await DEPLOY_LOCK_REF.get();
        const data = snap.data() || {};
        if (data.lockedBy === this.currentUserEmail) {
          await DEPLOY_LOCK_REF.set({ locked: false, lockedBy: null, lockedAt: null, serviceId: null });
        }
      } catch (e) {
        console.error("releaseDeployLock error:", e);
      }
    },
    subscribeDeployLock() {
      this.deployLockListener = DEPLOY_LOCK_REF.onSnapshot((snap) => {
        const data = snap.data() || {};
        this.deployLock = {
          lockedByName: data.lockedByName || null,
          locked: !!data.locked,
          lockedBy: data.lockedBy || null,
          lockedAt: data.lockedAt || null,
        };
      });
    },
    unsubscribeDeployLock() {
      if (this.deployLockListener) {
        this.deployLockListener();
        this.deployLockListener = null;
      }
    },
    async startDownload(row) {
      console.log(row, this.selectedBackup[row.domain])
      const snap = this.selectedBackup[row.domain];
      snap.domain = row.domain
      this.downloadLoading = row.domain;
      try {
        const {id} = await db.collection('jobLogs').add({})
        const res = await this.$http.post("/lxd/n8n-apis-for-lxd-migration", {
          grant_type: "token",
          action: "download-backup",
          serviceId: this.serviceId,
          domain: snap.domain,
          ObjID: snap.ObjID,
          jobId: id,
        });
        if (res.data && res.data.success) {
          const jobData = {
            ip: '',
            uid: '',
            date: moment().tz('America/New_York').format('YYYY-MM-DD'),
            domain: row.domain,
            inputDomain: row.domain,
            jobName: 'backup-download',
            status: 'pending',
            user: {
              firstName: '',
              lastName: '',
              id: '',
            },
            id,
            backupDate: snap.Time,
            backupName: `${snap.IsOwn ? 'manual' : 'back'}-${snap.Time.replace(/:/g, '_').replace(' ', 'T')}`,
            collaboratorUID: '',
            collaborator: '0',
            collaboratorEmail: '',
            createdAt: new Date().toISOString(),
            showStatus: false,
            from: { name: 'Terminated Backup Download' },
            logs: [],
            jobData: {
              operation: 'download-backup-for-terminated',
              email: this.user.email,
              objId: snap.ObjID,
              hostname: 'wpo-bs-backup-restore.bigscoots.dev',
              serviceId: this.serviceId,
              ip: '',
              domain: snap.domain,
              backupDate: snap.Time,
              backupName: `${snap.IsOwn ? 'manual' : 'back'}-${snap.Time.replace(/:/g, '_').replace(' ', 'T')}`,
              UID: '',
              jobName: 'backup-download',
              showCustomToast: false,
              collaboratorUID:  '',
              collaborator: '0',
              collaboratorEmail: ''
            }
          };
          await db.collection('jobLogs').doc(id).update(jobData);
          await db.collection('downloadedBackupsForTerminated').doc(id).set({
            jobId: id,
            status: 'running',
            serviceId: this.serviceId,
            ...snap,
            createdAt: new Date().toISOString(),
          })
          this.showToast({ message: "Download backup operation has started.", duration: 2000 }, true, "success");
        } else {
          this.showToast({ message: "Download failed.", duration: 2000 }, true, "danger");
        }
      } catch (e) {
        console.error(e);
        this.showToast(
          { message: (e.response && e.response.data && e.response.data.message) || "Download failed.", duration: 3000 },
          true,
          "danger"
        );
      } finally {
        this.downloadLoading = null;
      }
    },
    subscribeDownloadedBackups() {
      this.downloadedList = []
      const collectionRef = db
          .collection("downloadedBackupsForTerminated")
          .where('expiryDate', '>=', new Date().toISOString())
          .orderBy("expiryDate", "desc");

      const minLoaderMs = 400;
      const loaderStart = Date.now();
      this.downloadedBackupsListener = collectionRef.onSnapshot(
          (snapshot) => {
            this.downloadedList = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            const elapsed = Date.now() - loaderStart;
            if (elapsed >= minLoaderMs) {
              this.downloadedBackupsLoading = false;
            } else {
              setTimeout(() => {
                this.downloadedBackupsLoading = false;
              }, minLoaderMs - elapsed);
            }
            this.initReadyDownloadsDataTable();
          },
          (error) => {
            console.error("Error listening to backups:", error);
            this.downloadedBackupsLoading = false;
          }
      );
    },
    unsubscribeDownloadedBackups() {
      if (this.downloadedBackupsListener) {
        this.downloadedBackupsListener();
        this.downloadedBackupsListener = null;
      }
    },
  },
  beforeDestroy() {
    this.unsubscribeDownloadedBackups();
    this.unsubscribeDeployLock();
    if (this.$el) {
      const elms = this.$el.querySelectorAll(".terminated-backup-tooltip");
      elms.forEach((el) => {
        if (el._tippy) el._tippy.destroy();
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.terminated-accounts-backups {
  .form-card {
    margin-bottom: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);

    .form-card-header {
      background: #fff;
      border-bottom: 1px solid #e2e8f0;
      padding: 1.25rem 1.5rem;

      .card-title {
        font-weight: 700;
        color: #2d3748;
        font-size: 1.25rem;
      }
    }

    .form-card-body {
      padding: 1.5rem 1.5rem;
    }

    .form-description {
      color: #718096;
      font-size: 0.95rem;
      margin-bottom: 1.25rem;
    }
  }

  .terminated-form {
    display: flex;
    width: 30%;
    flex-direction: column;
    gap: 1rem;

    .form-group {
      margin-bottom: 0;
      margin-right: 0.5rem;
      min-width: 220px;

      label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 700;
        color: #2d3748;
        font-size: 0.9rem;
        letter-spacing: 0.01em;
      }
    }


    .deploy-hint {
      font-size: 0.75rem;
      color: #718096;
    }
  }

  .status-message {
    padding: 0.75rem 1rem;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;

    &.status-success {
      background: #d4edda;
      color: #155724;
      border: 1px solid #c3e6cb;
    }

    &.status-error {
      background: #f8d7da;
      color: #721c24;
      border: 1px solid #f5c6cb;
    }
  }

  .table-card {
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    &.downloaded-card {
      margin-top: 1.5rem;
    }

    .card-header {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-bottom: 1px solid #dee2e6;
      padding: 1rem 1.25rem;
      border-radius: 10px 10px 0 0;

      .card-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: #333;
      }
    }

    .backups-table {
      thead tr {
        background: #f8f9fa;
        border-bottom: 2px solid #dee2e6;

        th {
          font-weight: 600;
          color: black;
          text-transform: uppercase;
          font-size: 0.7rem;
          letter-spacing: 0.5px;
          padding: 1rem;
          border: none;
        }
      }

      tbody tr {
        transition: background-color 0.15s ease;

        td {
          padding: 1rem;
          vertical-align: middle;
        }
      }

      .domain-name {
        font-weight: 600;
        color: #333;
      }

      .backup-select {
        max-width: 320px;
        border-radius: 6px;
        border: 1px solid #dee2e6;
        padding: 0.4rem 0.75rem;
        font-size: 0.9rem;
      }


      .empty-state {
        padding: 3rem 1.5rem !important;
        text-align: center;
        color: #6c757d;

        p {
          font-size: 1rem;
        }

        small {
          display: block;
          margin-top: 0.25rem;
        }
      }
    }

    .downloaded-table .status-badge {
      font-size: 0.7rem;
      padding: 0.35em 0.65em;
      font-weight: 600;
    }
  }
}
</style>
